/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, useEffect } from 'react';
import {  
  Input, 
  Label, 
  Select, 
  SelectOption, 
} from '@forge/common';
import { FormElementType, IFormElementConfig } from '../../../models/softwaredetails';
import './DynamicForm.scss';

interface IDynamicFormElementProps extends IFormElementConfig {
  onChange: (value: string | number) => void;
  onBlur?: () => void;
  error?: string;
  touched?: boolean;
}

export const DynamicFormElement: React.FC<IDynamicFormElementProps> = ({
  type,
  id,
  label,
  placeholder,
  disabled = false,
  options = [],
  value,
  error,
  touched = false,
  onChange,
  onBlur
}) => {
  const [localValue, setLocalValue] = useState(value);

  // Only update localValue when component first mounts or id changes
  // This prevents typed text from disappearing and fixes focus issues
  useEffect(() => {
    setLocalValue(value);
  }, [id]); 

  const handleChange = (newValue: string | number) => {
    setLocalValue(newValue);
    onChange(newValue);
  };

  const renderInput = () => {
    switch (type) {
      case FormElementType.TEXT:
        return (
          <Input
            id={id}
            value={String(localValue ?? '')}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={onBlur}
            className="dynamic-form-input"
          />
        );

      case FormElementType.PASSWORD:
        return (
          <Input
            id={id}
            type="password"
            value={String(localValue ?? '')}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={onBlur}
            className="dynamic-form-input"
          />
        );

      case FormElementType.SELECT:
        return (
          <Select
            id={id}
            value={String(localValue ?? '')}
            disabled={disabled}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            className="dynamic-form-select"
          >
            {options && options.map((opt) => (
              <SelectOption key={opt.value} value={String(opt.value)} label={opt.text} />
            ))}
          </Select>
        );

      case FormElementType.INTEGER:
        return (
          <Input
            id={id}
            type="number"
            value={String(localValue ?? '')}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => {
              const numValue = parseInt(e.target.value, 10);
              handleChange(isNaN(numValue) ? '' : numValue);
            }}
            onBlur={onBlur}
            className="dynamic-form-input"
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="field-row">
      <Label htmlFor={id}>{label}</Label>
      <div className="input-wrapper">
        {renderInput()}
        {touched && error && (
          <div className="error-message">
            <span className="error-icon">⚠</span>
            <span className="error-text">{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};
